import {
  ChangeDetectionStrategy, Component, ElementRef, computed, effect, inject, input, output, signal, untracked,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdMoonTransitFixation, HdMoonTransitFormation } from './hd-moon-transit';
import { HD_MOON_TRANSIT_TYPE_COLORS } from './hd-moon-transit-card.models';
import {
  MOON_TRANSIT_TODAY_OBSTACLE_GATE,
  MoonTransitRingLayout,
  MoonTransitStackLayoutItem,
  moonTransitAnchorFractions,
  moonTransitRectAtRadius,
  moonTransitStackAnchorRadius,
  moonTransitTearOutPlacement,
  resolveMoonTransitStackAngles,
} from './hd-moon-transit-card-layout';
import { HdMoonTransitFormationClickEvent, HdMoonTransitPin } from './hd-moon-transit-view.models';
import {
  MOON_TRANSIT_CARD_ANCHOR_RADIUS,
  MOON_TRANSIT_MANDALA_GEOMETRY,
  MOON_TRANSIT_TODAY_ICON_EDGE_OFFSET,
  MOON_TRANSIT_WHEEL_EDGE_RADIUS,
  MandalaGeometryConfig,
} from '../mandala.constants';
import { buildMandalaGateSegments, longitudeToScreenAngle } from '../mandala-layout';
import { normalizeAngle, polarToCartesian } from '../mandala-geometry.utils';
import { HdChannelTranslation, HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '../../bodygraph/hd-planet-info.service';
import { HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { getHdPlanetDisplayConfig } from '../../bodygraph/planet-grid/hd-planet-display-config';
import { HdActivePaletteService } from '../../bodygraph/hd-active-palette.service';

/** Klik op een card: de formatie + cursorpositie (voor de floating dialog). */
export type HdMoonTransitCardClickEvent = HdMoonTransitFormationClickEvent;

interface MoonTransitFixationVm {
  readonly key: string;
  readonly arrow: '▲' | '▼';
  readonly glyph: string;
  readonly isDesign: boolean;
  readonly title: string;
}

interface MoonTransitCardVm {
  readonly formation: HdMoonTransitFormation;
  readonly key: string;
  /** Bv. "41-30 Recognition" — partnerpoort eerst, zoals in het referentiebeeld. */
  readonly title: string;
  readonly keynote: string;
  readonly typeColor: string;
  readonly centerIcons: readonly { readonly html: SafeHtml; readonly label: string }[];
  readonly fixations: readonly MoonTransitFixationVm[];
  readonly showBridge: boolean;
  readonly bridgeTooltip: string;
}

interface MoonTransitStackVm {
  /**
   * Track-key voor de template: auto-stapel `gate:<moonGate>` (ongepinde
   * formaties van één maanpoort samen), singleton `card:<channelKey>` (één
   * gepinde formatie los). De structuur wijzigt dus enkel bij pin-commit/-remove
   * — nooit tijdens een sleep, zodat het capture-dragende kaart-element blijft
   * bestaan.
   */
  readonly key: string;
  readonly moonGate: number;
  /** Gewenste anker-schermhoek: de poort-midAngle (collision kan verdraaien). */
  readonly midAngle: number;
  /**
   * Stapelrichting (finetune-ronde 5): kolom (default, cards van dezelfde
   * poort onder elkaar); in de absolute top-/bottom-sectie met 3+ cards een
   * grid van max 2 rijen dat horizontaal groeit — daar is verticale ruimte
   * schaars (scrollbar-gevaar) en breedte royaal.
   */
  readonly layout: 'column' | 'grid';
  readonly cards: readonly MoonTransitCardVm[];
  /** Gepinde singleton-formatie (eigen versleepbare card) of null voor een auto-stapel. */
  readonly singleton: HdMoonTransitFormation | null;
}

/** Opgeloste plaatsing van een stack: ringhoek + anker-straal (viewBox-units). */
interface MoonTransitStackPlacement {
  readonly angle: number;
  readonly radiusUnits: number;
}

/**
 * Lopende sleep van één formatie-card. De start-pointer en de meetgegevens
 * (host-rect + unitsToPx + wielcentrum in px + de start-rect van de kaart)
 * worden bij pointerdown gecachet zodat pointermove puur rekent. De kaart volgt
 * de cursor 1-op-1 via `dragDeltaPx` (pure translate) — stabiel overal, óók vlak
 * bij het wielcentrum. Enkel wanneer de radiale magneet actief is (`snapActive`,
 * dicht bij de default-ring en dus ver van het centrum) tekent de preview via de
 * gesnapte `currentPlacement`, zodat de kaart zichtbaar op de ring klikt. De sleep
 * wijzigt de DOM-structuur NIET (capture-veilig — het kaart-element blijft leven);
 * `movedPx` is de cumulatieve schermverplaatsing voor de klik≠pin-drempel.
 */
interface MoonTransitDragState {
  readonly channelKey: string;
  readonly pointerId: number;
  /** Pointer-px op pointerdown (host-relatief) — nulpunt voor de sleep-delta én de klik-drempel. */
  readonly startPointerPx: { readonly x: number; readonly y: number };
  /** Meet-cache: hostRect-oorsprong, px-per-viewBox-unit en wielcentrum in host-px. */
  readonly hostLeft: number;
  readonly hostTop: number;
  readonly unitsToPx: number;
  readonly centerPx: { readonly x: number; readonly y: number };
  /** Kaart-rect (host-relatief px) op pointerdown: ijkpunt voor de transform-preview én de sleep-delta. */
  readonly originalCardRectPx: { readonly left: number; readonly top: number; readonly width: number; readonly height: number };
  /** Cumulatieve pointer-verplaatsing (host-px) sinds pointerdown: dé pure-translate preview. */
  readonly dragDeltaPx: { readonly x: number; readonly y: number };
  /** Ligt de (ongesnapte) straal binnen de magneet-tolerantie van de default-ring? Dan wint de gesnapte reconstructie-preview. */
  readonly snapActive: boolean;
  readonly currentPlacement: MoonTransitStackPlacement;
  readonly movedPx: number;
}

/** Geschatte beeldvullende grootte (fractie van de laag) van het maan-icoon. */
const TODAY_ICON_SIZE_FRACTION = 0.034;

/** Extra marge (viewBox-units) tussen wielrand en card-rects (ronde 5). */
const WHEEL_CLEARANCE_GAP_UNITS = 2;

/** Schermverplaatsing (px) waaronder een pointerup als klik telt, niet als sleep. */
const MOON_TRANSIT_CARD_DRAG_THRESHOLD_PX = 6;

/** Radiale magneet-tolerantie (viewBox-units): binnen dit bereik snapt de straal op de default-ring. */
const MOON_TRANSIT_CARD_RADIAL_SNAP_UNITS = 8;

/**
 * HTML-overlay met de formatie-cards rond het mandala-wiel (spec
 * `2026-07-06-hd-moon-transit-chart-design.md` §6.3) — zelfde patroon als de
 * gate-block-layer op de bodygraph. Cards stapelen per maanpoort en ankeren
 * radiaal buiten de ringen op de poort-hoek; klik opent de kanaal-info dialog
 * via {@link formationClicked} (§6.6).
 */
@Component({
  selector: 'hd-moon-transit-card-layer',
  imports: [SharedModule],
  templateUrl: './hd-moon-transit-card-layer.component.html',
  styleUrl: './hd-moon-transit-card-layer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.move-active]': 'moveToolActive()',
    '[class.dragging]': 'dragState() !== null',
  },
})
export class HdMoonTransitCardLayerComponent {
  readonly formations = input.required<readonly HdMoonTransitFormation[]>();
  readonly geometry = input<MandalaGeometryConfig>(MOON_TRANSIT_MANDALA_GEOMETRY);

  /**
   * Longitude van de today-marker: het maan-icoon op het wiel wordt dan als
   * vast obstakel in de collision-detectie meegenomen, zodat geen enkele card
   * het icoon (en zijn hover-tooltip) kan bedekken. `null` = geen obstakel.
   */
  readonly todayLongitude = input<number | null>(null);

  /**
   * ChannelKeys van formaties die een highlight-border krijgen (hover op de
   * connector-lijn of de maanpoort in het wiel — finetune-ronde 2).
   */
  readonly highlightedChannelKeys = input<ReadonlySet<string>>(new Set<string>());

  readonly formationClicked = output<HdMoonTransitCardClickEvent>();

  /**
   * Hover op een card (spec-aanvulling finetune 2026-07-06): de page kleurt
   * het kanaalstuk van de maanpoort in de bodygraph transit-blauw. `null` bij
   * mouseleave.
   */
  readonly formationHovered = output<HdMoonTransitFormation | null>();

  /**
   * Handmatig gepinde stacks (polair, per maanpoort): winnen van de
   * collision-oplossing/default en tellen als vaste obstakels mee. De page
   * bindt dit uit de chart-preset (drag & pin, plan 2026-07-07).
   */
  readonly pins = input<readonly HdMoonTransitPin[]>([]);

  /**
   * Verplaats-tool: alléén dan zijn de stacks sleepbaar. Enkel de klik-dialog
   * pauzeert onder de tool; de hover-keten blijft live, zodat de highlight
   * (blauw kanaalstuk + connector/kaart-keten) tijdens het schikken zichtbaar blijft.
   */
  readonly moveToolActive = input(false);

  /** Sleep afgerond → pin (ringhoek + straal in viewBox-units, per formatie). */
  readonly pinCommitted = output<HdMoonTransitPin>();
  /** Dubbelklik op een gepinde card → unpin (per formatie). */
  readonly pinRemoved = output<{ channelKey: string }>();

  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly planetInfoService = inject(HdPlanetInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly centerColors = inject(HdActivePaletteService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * Opgeloste plaatsing per maanpoort uit de collision-detectie: na elke
   * render met nieuwe stacks worden de stack-afmetingen gemeten en draaien
   * botsende stacks lángs de wielrand van elkaar weg (finetune-ronde 4) —
   * positie én corner-hug-uitlijning bewegen mee, dus cards blijven het wiel
   * omarmen. De straal bevat de radiale uitwijk die wiel-overlap op diagonale
   * hoeken voorkomt (ronde 5). De pure berekening zit in
   * `resolveMoonTransitStackAngles`/`moonTransitStackAnchorRadius`.
   */
  private readonly resolvedPlacements = signal<ReadonlyMap<number, MoonTransitStackPlacement>>(new Map());

  /** Lopende sleep (of `null`); de transiente plaatsing wint in {@link effectivePlacement}. */
  protected readonly dragState = signal<MoonTransitDragState | null>(null);

  /** Pin per formatie (channelKey) — snelle lookup voor plaatsing én obstakel-opbouw. */
  private readonly pinsByChannelKey = computed<ReadonlyMap<string, HdMoonTransitPin>>(
    () => new Map(this.pins().map(pin => [pin.channelKey, pin])),
  );

  constructor() {
    effect(() => {
      // Her-resolvet enkel wanneer de wéreld verandert waardoor de kaart-MATEN of de
      // vaste obstakels wijzigen: de formaties, de wiel-geometrie, de content-taal en
      // de (async ladende) kanaal-vertalingen — die bepalen titel/keynote en dus de
      // cardbreedte — plus de today-marker. Bewust NIET getrackt:
      // • pins(): een pin-mutatie her-resolvet bewust niets. Handmatig schikken mag de
      //   automatisch berekende posities van de overige stapels niet aanraken; de
      //   overgebleven (tear-out) of terugkerende (unpin) kaart blijft staan via het
      //   compensatie-anker dat die acties zelf in `resolvedPlacements` schrijven. Een
      //   expliciete herberekening loopt via {@link recomputeAutoPlacements}.
      // • dragState: een sleep is een pure transform-preview en wijzigt de structuur
      //   niet, dus mag geen rAF-hermeet-storm per pointermove uitlokken.
      this.formations();
      this.geometry();
      this.todayLongitude();
      const lang = this.contentLang.effectiveLang();
      this.gateInfoService.channelTranslationsIn(lang);
      untracked(() => {
        // resolvedPlacements NIET leegmaken vóór de hermeting: de oude plaatsing blijft
        // staan tot de verse meting klaar is, zodat de overige stapels niet even naar
        // hun default-hoek flitsen. Stale entries voor verdwenen poorten zijn
        // onschadelijk (de lookup loopt via de bestaande stacks).
        requestAnimationFrame(() => this.measureAndResolveCollisions());
      });
    });

    // Tool uit → een lopende sleep annuleren zonder commit (plan §9, zoals de
    // gate-block-layer). `untracked` zodat de effect enkel op `moveToolActive`
    // reageert, niet op `dragState`.
    effect(() => {
      if (!this.moveToolActive()) {
        untracked(() => this.dragState.set(null));
      }
    });
  }

  /**
   * Render-structuur (hangt enkel aan formaties + pins, NIET aan de sleep): per
   * maanpoort één auto-stapel met de óngepinde formaties, plus één losse
   * singleton-stapel per gepinde formatie. Zo verlaat een gepinde card zijn
   * stapel en is elk kaartje apart plaatsbaar. De structuur verandert dus enkel
   * bij pin-commit/-remove — tijdens een sleep blijft ze intact (het gesleepte
   * kaart-element blijft leven → pointer-capture houdt stand).
   */
  protected readonly renderStacks = computed<MoonTransitStackVm[]>(() => {
    const formations = this.formations();
    if (formations.length === 0) {
      return [];
    }
    const geometry = this.geometry();
    const lang = this.contentLang.effectiveLang();
    const channelTranslations = this.gateInfoService.channelTranslationsIn(lang);
    const segments = new Map(buildMandalaGateSegments(geometry).map(segment => [segment.gate, segment]));
    const pinnedKeys = this.pinsByChannelKey();

    const byGate = new Map<number, HdMoonTransitFormation[]>();
    for (const formation of formations) {
      const existing = byGate.get(formation.moonGate);
      if (existing) {
        existing.push(formation);
      } else {
        byGate.set(formation.moonGate, [formation]);
      }
    }

    const stacks: MoonTransitStackVm[] = [];
    for (const [moonGate, group] of byGate) {
      const segment = segments.get(moonGate);
      if (segment == null) {
        continue;
      }
      const autoGroup = group.filter(formation => !pinnedKeys.has(formation.channelKey));
      // Lege auto-stapels weglaten (alle formaties van deze poort zijn gepind).
      if (autoGroup.length > 0) {
        stacks.push({
          key: `gate:${moonGate}`,
          moonGate,
          midAngle: segment.midAngle,
          layout: stackLayout(segment.midAngle, autoGroup.length),
          cards: autoGroup.map(formation => this.buildCard(formation, channelTranslations, lang)),
          singleton: null,
        });
      }
      for (const formation of group) {
        if (pinnedKeys.has(formation.channelKey)) {
          stacks.push({
            key: `card:${formation.channelKey}`,
            moonGate,
            midAngle: segment.midAngle,
            layout: 'column',
            cards: [this.buildCard(formation, channelTranslations, lang)],
            singleton: formation,
          });
        }
      }
    }
    return stacks;
  });

  protected onCardClick(formation: HdMoonTransitFormation, event: MouseEvent): void {
    // Onder de verplaats-tool enkel de kanaal-info-dialog onderdrukken (plan §9):
    // een sleep mag niet per ongeluk als klik eindigen. De hover-keten pauzeert
    // hier níét mee — die blijft onder de tool gewoon live.
    if (this.moveToolActive()) {
      return;
    }
    this.formationClicked.emit({ formation, origin: { x: event.clientX, y: event.clientY } });
  }

  protected onCardEnter(formation: HdMoonTransitFormation): void {
    // Hover publiceert de bodygraph-highlight óók onder de verplaats-tool, zodat de
    // highlight-keten (blauw kanaalstuk + connector/kaart) tijdens het schikken blijft.
    this.formationHovered.emit(formation);
  }

  protected onCardLeave(): void {
    this.formationHovered.emit(null);
  }

  /**
   * Effectieve plaatsing: een gepinde singleton wint met zijn pin, een
   * auto-stapel volgt de collision-opgeloste hoek/straal (of de defaults). De
   * sleep zit hier NIET in — die is een pure transform-preview op de kaart zelf
   * ({@link cardTransform}), zodat de stapel-positie en de meting ongemoeid
   * blijven tijdens de gesture.
   */
  private effectivePlacement(stack: MoonTransitStackVm): MoonTransitStackPlacement {
    if (stack.singleton) {
      const pin = this.pinsByChannelKey().get(stack.singleton.channelKey);
      if (pin) {
        return { angle: pin.angleDeg, radiusUnits: pin.radiusUnits };
      }
    }
    return this.resolvedPlacements().get(stack.moonGate)
      ?? { angle: stack.midAngle, radiusUnits: MOON_TRANSIT_CARD_ANCHOR_RADIUS };
  }

  /** Gepinde singleton → amber pin-dot (alleen zichtbaar met actieve tool). */
  protected isPinned(stack: MoonTransitStackVm): boolean {
    return stack.singleton != null && this.pinsByChannelKey().has(stack.singleton.channelKey);
  }

  /** De kaart die nu gesleept wordt (transform-preview) — of niet. */
  protected isDragging(card: MoonTransitCardVm): boolean {
    return this.dragState()?.channelKey === card.key;
  }

  /** De stapel bevat de gesleepte kaart → tijdelijk boven de overige stapels (z-index). */
  protected stackHasDrag(stack: MoonTransitStackVm): boolean {
    const key = this.dragState()?.channelKey;
    return key != null && stack.cards.some(card => card.key === key);
  }

  /**
   * Sleep-preview: verschuif de gesleepte kaart met een pure translate naar de
   * exacte plek waar zijn singleton-stapel ná de commit zal renderen (corner-hug
   * op de huidige plaatsing). Zo is de drop naadloos — de kaart staat al op zijn
   * eindpositie op het moment dat de structuur wisselt. Aanname: de
   * transform-keten boven de kaart (stapel `left%/top%` + anchorTransform) bevat
   * enkel translaties, geen scale, dus een px-translate op de kaart is 1-op-1 een
   * schermverplaatsing.
   */
  protected cardTransform(card: MoonTransitCardVm): string | null {
    const drag = this.dragState();
    if (!drag || drag.channelKey !== card.key) {
      return null;
    }
    // Buiten de magneet-zone: pure pointer-translate — glijdt exact met de cursor mee en is
    // stabiel tot in het wielcentrum (geen center-vortex). In de magneet-zone (dicht bij de
    // default-ring, ver van het centrum): teken via de gesnapte plaatsing zodat de kaart
    // zichtbaar op de ring klikt en de drop naadloos op de singleton-render valt.
    if (drag.snapActive) {
      const preview = this.previewCardTopLeft(drag);
      const dx = preview.left - drag.originalCardRectPx.left;
      const dy = preview.top - drag.originalCardRectPx.top;
      return `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px)`;
    }
    return `translate(${drag.dragDeltaPx.x.toFixed(2)}px, ${drag.dragDeltaPx.y.toFixed(2)}px)`;
  }

  /** Toekomstige (host-relatieve) top-left van de gesleepte kaart voor {@link cardTransform}. */
  private previewCardTopLeft(drag: MoonTransitDragState): { left: number; top: number } {
    const { angle, radiusUnits } = drag.currentPlacement;
    const anchor = polarToCartesian(drag.centerPx.x, drag.centerPx.y, radiusUnits * drag.unitsToPx, angle);
    const { fx, fy } = moonTransitAnchorFractions(angle);
    return {
      left: anchor.x - fx * drag.originalCardRectPx.width,
      top: anchor.y - fy * drag.originalCardRectPx.height,
    };
  }

  protected onCardPointerDown(card: MoonTransitCardVm, event: PointerEvent): void {
    if (!this.moveToolActive()) {
      return;
    }
    event.preventDefault();
    // Capture op de kaart-button zelf: die blijft de hele gesture leven (de
    // structuur wijzigt pas bij commit), dus de capture valt niet weg zoals bij
    // een mid-gesture herstructurering.
    const cardEl = (event.target as HTMLElement).closest('.hd-moon-transit-card') as HTMLElement | null;
    if (!cardEl) {
      return;
    }
    // Capture houdt de pointermove/up bij de laag ook als de cursor de kaart
    // verlaat; een mislukte capture (bv. een niet-actieve pointer) mag de sleep
    // echter niet afbreken — dan degradeert ze gewoon tot cursor-over-de-laag.
    try {
      cardEl.setPointerCapture(event.pointerId);
    } catch {
      // negeren: geen capture, sleep gaat door
    }
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const geometry = this.geometry();
    const unitsToPx = hostRect.width / geometry.viewBoxSize;
    const centerPx = { x: geometry.center.x * unitsToPx, y: geometry.center.y * unitsToPx };
    const cardRect = cardEl.getBoundingClientRect();
    const originalCardRectPx = {
      left: cardRect.left - hostRect.left,
      top: cardRect.top - hostRect.top,
      width: cardRect.width,
      height: cardRect.height,
    };
    // Tear-out zonder sprong: leid de startplaatsing af uit de huidige kaartpositie,
    // zodat de preview exact op de kaart begint. De sleep verschuift daarna die
    // start-rect 1-op-1 met de pointer (zie {@link onLayerPointerMove}).
    const { placement } = this.tearOutPlacement(originalCardRectPx, centerPx, unitsToPx);
    const pointerPx = { x: event.clientX - hostRect.left, y: event.clientY - hostRect.top };
    this.dragState.set({
      channelKey: card.key,
      pointerId: event.pointerId,
      startPointerPx: pointerPx,
      hostLeft: hostRect.left,
      hostTop: hostRect.top,
      unitsToPx,
      centerPx,
      originalCardRectPx,
      dragDeltaPx: { x: 0, y: 0 },
      snapActive: false,
      currentPlacement: placement,
      movedPx: 0,
    });
  }

  /**
   * Herleidt uit een rect (host-relatief px) het {hoek, straal}-anker dat een stapel
   * van die maat corner-hugged op precies die plek plaatst: neem het rect-centrum als
   * bootstrap-schermhoek, construeer daaruit het ankerpunt (fracties × maat) en
   * herleid hoek + straal; twee iteraties (constructie + één verfijning) volstaan — de
   * fractie-afhankelijkheid van de hoek is zwak. Gebruikt (a) bij de tear-out-start uit
   * de kaartpositie (een kleine restafwijking werkt de sleep meteen weg) en (b) voor
   * het compensatie-anker uit de union-rect van de over­blijvende/zittende kaarten
   * ({@link compensateAutoStackAnchor}).
   */
  private tearOutPlacement(
    rect: { left: number; top: number; width: number; height: number },
    centerPx: { x: number; y: number },
    unitsToPx: number,
  ): { placement: MoonTransitStackPlacement; anchorPx: { x: number; y: number } } {
    // Gedeelde corner-hug-math met de just-now-kaartlaag.
    return moonTransitTearOutPlacement(rect, centerPx, unitsToPx);
  }

  protected onLayerPointerMove(event: PointerEvent): void {
    const drag = this.dragState();
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    const pointerPx = { x: event.clientX - drag.hostLeft, y: event.clientY - drag.hostTop };
    // De kaart volgt de cursor 1-op-1: `dragDeltaPx` is de pure pointer-verplaatsing (die
    // voedt de preview — zie cardTransform). Uit de meegeschoven rect leiden we hoek/straal
    // af (zelfde corner-hug-math als de tear-out) voor de commit én de magneet-check.
    // Bewust GEEN polair-reconstructie voor de preview buiten de magneet-zone: dicht bij het
    // wielcentrum ontaardt die (atan2-singulariteit → de corner-hug-hoek klapt om, de kaart
    // springt ~een kaartbreedte weg = "center-vortex"). De pure-translate preview is daar
    // stabiel; de vroegere anker-greep dreef bovendien weg doordat de corner-hug-fracties
    // met de sleephoek meebewegen — beide zijn hiermee opgelost.
    const dx = pointerPx.x - drag.startPointerPx.x;
    const dy = pointerPx.y - drag.startPointerPx.y;
    const previewRect = {
      left: drag.originalCardRectPx.left + dx,
      top: drag.originalCardRectPx.top + dy,
      width: drag.originalCardRectPx.width,
      height: drag.originalCardRectPx.height,
    };
    const { placement } = this.tearOutPlacement(previewRect, drag.centerPx, drag.unitsToPx);
    // Radiale magneet: dicht bij de default-ring exact terugsnappen op de rand. De snap-zone
    // ligt ver van het centrum, dus enkel dáár is de reconstructie-preview veilig.
    const snapActive = Math.abs(placement.radiusUnits - MOON_TRANSIT_CARD_ANCHOR_RADIUS) <= MOON_TRANSIT_CARD_RADIAL_SNAP_UNITS;
    const radiusUnits = snapActive ? MOON_TRANSIT_CARD_ANCHOR_RADIUS : placement.radiusUnits;
    const movedPx = Math.max(drag.movedPx, Math.hypot(dx, dy));
    this.dragState.set({
      ...drag,
      dragDeltaPx: { x: dx, y: dy },
      currentPlacement: { angle: placement.angle, radiusUnits },
      snapActive,
      movedPx,
    });
  }

  protected onLayerPointerUp(event: PointerEvent): void {
    const drag = this.dragState();
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    this.dragState.set(null);
    if (drag.movedPx < MOON_TRANSIT_CARD_DRAG_THRESHOLD_PX) {
      return; // klik ≠ pin: anders pint elke (dubbel)klik de kaart op zijn auto-plek vast
    }
    // Compensatie-anker vóór de commit: komt de gesleepte kaart uit een auto-stapel
    // met ≥2 kaarten, dan krimpt die stapel zodra de pin-input binnenkomt. Meet nú de
    // overblijvende kaart(en) — pre-drop DOM; de transform-preview van de gesleepte
    // kaart verschuift de layout-slots van zijn buren niet — en anker de gekrompen
    // stapel daarop, zodat de overgebleven kaart exact blijft staan i.p.v. mee te
    // herberekenen (Stevens bug). Een gepinde singleton die opnieuw versleept wordt
    // heeft geen reststapel en valt buiten deze tak. Meetgegevens uit de sleep-cache
    // (host-oorsprong + unitsToPx + wielcentrum) — dezelfde ijking als de preview.
    const stacks = this.renderStacks();
    const sourceIndex = stacks.findIndex(
      stack => stack.singleton == null && stack.cards.some(card => card.key === drag.channelKey),
    );
    if (sourceIndex >= 0 && stacks[sourceIndex].cards.length >= 2) {
      this.compensateAutoStackAnchor(
        stacks[sourceIndex].moonGate,
        this.stackCardNodes(sourceIndex, drag.channelKey),
        drag.centerPx, drag.unitsToPx, drag.hostLeft, drag.hostTop,
      );
    }
    this.pinCommitted.emit({
      channelKey: drag.channelKey,
      angleDeg: drag.currentPlacement.angle,
      radiusUnits: drag.currentPlacement.radiusUnits,
    });
  }

  protected onCardDblClick(card: MoonTransitCardVm): void {
    // Enkel een gepinde kaart kan ontpind worden; ongepinde stapel-genoten negeren.
    if (!this.moveToolActive() || !this.pinsByChannelKey().has(card.key)) {
      return;
    }
    // Compensatie-anker vóór de unpin: de kaart keert terug in de auto-stapel van zijn
    // maanpoort. Bestaat die stapel al (≥1 ongepinde kaart van dezelfde poort), anker
    // hem dan op de union van zijn HUIDIGE kaarten zodat die blijven staan en de
    // terugkerende kaart aansluit. Bestaat er geen auto-stapel (alles was gepind), dan
    // laten we de oude resolved-entry of de default het opvangen (geen meting nodig).
    const moonGate = card.formation.moonGate;
    const autoIndex = this.renderStacks().findIndex(
      stack => stack.singleton == null && stack.moonGate === moonGate,
    );
    if (autoIndex >= 0) {
      const hostRect = this.host.nativeElement.getBoundingClientRect();
      const geometry = this.geometry();
      const unitsToPx = hostRect.width / geometry.viewBoxSize;
      const centerPx = { x: geometry.center.x * unitsToPx, y: geometry.center.y * unitsToPx };
      this.compensateAutoStackAnchor(
        moonGate, this.stackCardNodes(autoIndex, null),
        centerPx, unitsToPx, hostRect.left, hostRect.top,
      );
    }
    this.pinRemoved.emit({ channelKey: card.key });
  }

  protected stackLeftPercent(stack: MoonTransitStackVm): number {
    return this.anchorPercent(stack).x;
  }

  protected stackTopPercent(stack: MoonTransitStackVm): number {
    return this.anchorPercent(stack).y;
  }

  /** Corner-hug-uitlijning op de effectieve ringhoek (draait mee met de collision-oplossing). */
  protected stackTransform(stack: MoonTransitStackVm): string {
    return anchorTransform(this.effectivePlacement(stack).angle);
  }

  private anchorPercent(stack: MoonTransitStackVm): { x: number; y: number } {
    const geometry = this.geometry();
    const placement = this.effectivePlacement(stack);
    const position = polarToCartesian(
      geometry.center.x, geometry.center.y, placement.radiusUnits, placement.angle,
    );
    return { x: (position.x / geometry.viewBoxSize) * 100, y: (position.y / geometry.viewBoxSize) * 100 };
  }

  /** Ring-layout in host-px voor de gegeven host-maat. */
  private ringLayout(hostRect: DOMRect): MoonTransitRingLayout {
    const geometry = this.geometry();
    const unitsToPx = hostRect.width / geometry.viewBoxSize;
    return {
      centerX: (geometry.center.x / geometry.viewBoxSize) * hostRect.width,
      centerY: (geometry.center.y / geometry.viewBoxSize) * hostRect.height,
      anchorRadius: MOON_TRANSIT_CARD_ANCHOR_RADIUS * unitsToPx,
      wheelClearanceRadius: (MOON_TRANSIT_WHEEL_EDGE_RADIUS + WHEEL_CLEARANCE_GAP_UNITS) * unitsToPx,
    };
  }

  /**
   * Collision-invoer: het maan-icoon en elke gepinde singleton als vast obstakel
   * (expliciete px-rect), de auto-stapels als beweegbare items op hun poort-hoek.
   * Een gepinde singleton ankert op zíjn pin-straal (niet de wiel-vrijwarende
   * auto-straal) — de rect komt daarom analytisch uit {@link moonTransitRectAtRadius}
   * met de gemeten card-maten. De `querySelectorAll('.hd-moon-transit-stack')`
   * loopt 1-op-1 met {@link renderStacks}. Afgezonderd zodat de obstakel-opbouw
   * los van de DOM-meting testbaar is.
   */
  protected buildCollisionItems(hostRect: DOMRect): MoonTransitStackLayoutItem[] {
    const geometry = this.geometry();
    const unitsToPx = hostRect.width / geometry.viewBoxSize;
    const ring = this.ringLayout(hostRect);
    const stacks = this.renderStacks();
    const nodes = this.host.nativeElement.querySelectorAll<HTMLElement>('.hd-moon-transit-stack');
    // Het maan-icoon eerst als vast obstakel, zodat elke botsende stack ervan
    // wegdraait (het icoon en zijn tooltip blijven altijd vrij).
    const items: MoonTransitStackLayoutItem[] = [...this.todayObstacleItems(hostRect)];
    const pinsByChannelKey = this.pinsByChannelKey();
    nodes.forEach((node, index) => {
      const stack = stacks[index];
      if (stack == null) {
        return;
      }
      // Enkel de afmetingen komen uit de DOM; posities zijn analytisch uit de
      // ringhoek afleidbaar (en de card-maten zijn positie-onafhankelijk).
      const rect = node.getBoundingClientRect();
      const pin = stack.singleton ? pinsByChannelKey.get(stack.singleton.channelKey) : undefined;
      if (pin) {
        // Gepinde singleton = vast obstakel op zijn eigen straal; auto-stapels
        // wijken eromheen (plan §3). Het maanpoort-nummer volstaat als sorteersleutel
        // — vaste items vallen sowieso buiten de opgeloste-hoeken-map.
        items.push({
          gate: stack.moonGate,
          desiredAngle: pin.angleDeg,
          width: rect.width,
          height: rect.height,
          fixed: true,
          rect: moonTransitRectAtRadius(rect.width, rect.height, pin.angleDeg, ring, pin.radiusUnits * unitsToPx),
        });
        return;
      }
      items.push({
        gate: stack.moonGate,
        desiredAngle: stack.midAngle,
        width: rect.width,
        height: rect.height,
      });
    });
    return items;
  }

  /**
   * Expliciete herberekening van de automatische kaartposities (contextmenu
   * "Automatische transitkaarten herberekenen"): meet en her-resolvet alle
   * auto-stapels rond de gepinde kaarten. Pin-mutaties zelf doen dit bewust
   * nooit (zie het meet-effect).
   */
  recomputeAutoPlacements(): void {
    requestAnimationFrame(() => this.measureAndResolveCollisions());
  }

  /**
   * Compensatie-anker voor een auto-stapel die zo dadelijk van maat verandert
   * (tear-out laat hem krimpen, unpin laat hem groeien): meet de union-rect
   * (host-relatief px) van de opgegeven kaart-DOM-nodes, herleid daaruit met de
   * tear-out-math het {hoek, straal}-anker en schrijf dat immutably in
   * {@link resolvedPlacements} onder `moonGate`. Zo rendert de stapel ná de
   * structuurwissel met een anker dat de gemeten kaarten op hun huidige plek houdt,
   * los van elke automatische herberekening.
   *
   * Exact wanneer de union één kaart is (tear-out met rest = 1 — Stevens geval — of
   * een unpin naar een 1-kaart-doelstapel). Bij ≥2 kaarten wordt enkel de
   * union-bounding-box verankerd: het interne gat dat de weggenomen kaart achterlaat
   * (tear-out) resp. de invoeg-verschuiving van de terugkerende kaart (unpin) is
   * geometrisch onvermijdelijk zolang de stapel gepakt getekend wordt.
   */
  private compensateAutoStackAnchor(
    moonGate: number,
    cardNodes: readonly HTMLElement[],
    centerPx: { readonly x: number; readonly y: number },
    unitsToPx: number,
    hostLeft: number,
    hostTop: number,
  ): void {
    if (cardNodes.length === 0 || unitsToPx <= 0) {
      return;
    }
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    for (const node of cardNodes) {
      const rect = node.getBoundingClientRect();
      left = Math.min(left, rect.left - hostLeft);
      top = Math.min(top, rect.top - hostTop);
      right = Math.max(right, rect.right - hostLeft);
      bottom = Math.max(bottom, rect.bottom - hostTop);
    }
    const union = { left, top, width: right - left, height: bottom - top };
    const { placement } = this.tearOutPlacement(union, centerPx, unitsToPx);
    const next = new Map(this.resolvedPlacements());
    next.set(moonGate, placement);
    this.resolvedPlacements.set(next);
  }

  /**
   * De kaart-DOM-nodes van de stapel op `stackIndex` (host-index-consistent met
   * {@link renderStacks}, net als {@link buildCollisionItems}), optioneel zonder de
   * kaart met `excludeKey` (de gesleepte kaart bij tear-out). Leeg wanneer de
   * stapel(node) ontbreekt.
   */
  private stackCardNodes(stackIndex: number, excludeKey: string | null): HTMLElement[] {
    const stack = this.renderStacks()[stackIndex];
    const stackNode = this.host.nativeElement
      .querySelectorAll<HTMLElement>('.hd-moon-transit-stack')[stackIndex];
    if (stack == null || stackNode == null) {
      return [];
    }
    const cardNodes = Array.from(stackNode.querySelectorAll<HTMLElement>('.hd-moon-transit-card'));
    const result: HTMLElement[] = [];
    stack.cards.forEach((card, index) => {
      const node = cardNodes[index];
      if (node != null && card.key !== excludeKey) {
        result.push(node);
      }
    });
    return result;
  }

  private measureAndResolveCollisions(): void {
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    // Niets meten in een (nog) niet uitgelegde container (bv. TestBed zonder maat).
    if (hostRect.width < 50 || hostRect.height < 50) {
      return;
    }
    const geometry = this.geometry();
    const unitsToPx = hostRect.width / geometry.viewBoxSize;
    const ring = this.ringLayout(hostRect);
    const items = this.buildCollisionItems(hostRect);
    const angles = resolveMoonTransitStackAngles(items, ring);
    // Volledige plaatsing per vrije stack: opgeloste hoek + de anker-straal mét
    // radiale uitwijk (wiel-vrijwaring) voor die hoek en deze card-maten. Gepinde
    // stacks krijgen hun plaatsing rechtstreeks uit de pin (niet hier).
    const placements = new Map<number, MoonTransitStackPlacement>();
    for (const item of items) {
      if (item.fixed) {
        continue;
      }
      const angle = angles.get(item.gate) ?? item.desiredAngle;
      const radiusPx = moonTransitStackAnchorRadius(item.width, item.height, angle, ring);
      placements.set(item.gate, { angle, radiusUnits: radiusPx / unitsToPx });
    }
    if (placements.size > 0) {
      this.resolvedPlacements.set(placements);
    }
  }

  /** Phantom-obstakel (px-rect) rond het today-maanicoon, of leeg zonder maanpositie. */
  private todayObstacleItems(hostRect: DOMRect): MoonTransitStackLayoutItem[] {
    const todayLongitude = this.todayLongitude();
    if (todayLongitude == null) {
      return [];
    }
    const geometry = this.geometry();
    const angle = longitudeToScreenAngle(todayLongitude);
    const iconRadius = MOON_TRANSIT_WHEEL_EDGE_RADIUS + MOON_TRANSIT_TODAY_ICON_EDGE_OFFSET;
    const position = polarToCartesian(geometry.center.x, geometry.center.y, iconRadius, angle);
    const size = hostRect.width * TODAY_ICON_SIZE_FRACTION;
    return [{
      gate: MOON_TRANSIT_TODAY_OBSTACLE_GATE,
      desiredAngle: angle,
      width: size,
      height: size,
      fixed: true,
      rect: {
        left: (position.x / geometry.viewBoxSize) * hostRect.width - size / 2,
        top: (position.y / geometry.viewBoxSize) * hostRect.height - size / 2,
        width: size,
        height: size,
      },
    }];
  }

  private buildCard(
    formation: HdMoonTransitFormation,
    channelTranslations: Readonly<Record<string, HdChannelTranslation>>,
    lang: string,
  ): MoonTransitCardVm {
    const translation = channelTranslations[formation.channelKey];
    return {
      formation,
      key: formation.channelKey,
      title: `${formation.partnerGate}-${formation.moonGate} ${translation?.name ?? ''}`.trim(),
      keynote: translation?.shortDescription ?? '',
      typeColor: HD_MOON_TRANSIT_TYPE_COLORS[formation.manifestationType],
      centerIcons: formation.newlyDefinedCenters.map(center => ({
        html: this.centerColors.iconHtml(center),
        label: this.gateInfoService.centerLabelIn(center, lang),
      })),
      fixations: formation.fixations.map((fixation, index) => this.buildFixationVm(fixation, index, lang)),
      showBridge: formation.bridgesSplit,
      bridgeTooltip: this.t('humanDesign.chart.moonTransit.bridgeTooltip'),
    };
  }

  private buildFixationVm(fixation: HdMoonTransitFixation, index: number, lang: string): MoonTransitFixationVm {
    const planetName = fixation.planetKey
      ? this.planetInfoService.planetInfoIn(fixation.planetKey, lang)?.name ?? fixation.planetKey
      : '';
    const kindLabel = this.t(
      fixation.kind === 'exaltation'
        ? 'humanDesign.chart.moonTransit.fixationExaltation'
        : 'humanDesign.chart.moonTransit.fixationDetriment',
    );
    return {
      key: `${fixation.side}-${fixation.planetId}-${fixation.line}-${fixation.kind}-${index}`,
      arrow: fixation.kind === 'exaltation' ? '▲' : '▼',
      glyph: fixation.planetKey ? getHdPlanetDisplayConfig(fixation.planetKey).astronomiconChar : '',
      isDesign: fixation.side === 'design',
      title: [planetName, `${this.t('humanDesign.chart.channelInfo.line')} ${fixation.line}`, kindLabel]
        .filter(part => part.length > 0)
        .join(' · '),
    };
  }

  private t(key: string): string {
    return this.transloco.translate(key, undefined, this.contentLang.effectiveLang());
  }
}

/**
 * Uitlijning van de card-stack t.o.v. zijn anker: een vloeiende functie van de
 * schermhoek (0° = boven, met de klok mee) die de stack altijd radiaal naar
 * búiten laat uitsteken. Bovenaan hangt hij gecentreerd boven het anker,
 * rechts steekt hij naar rechts uit, en op tussenhoeken (bv. 45°) raakt de
 * dichtstbijzijnde card-hóék het anker — zo staat de card zo dicht mogelijk
 * bij zijn maanpoort (finetune-ronde 3, i.p.v. de oude 4-kwadranten-snap).
 * De fracties zijn gedeeld met de analytische rect-berekening van de
 * collision-resolver, zodat render en berekening exact overeenkomen.
 */
function anchorTransform(angle: number): string {
  const { fx, fy } = moonTransitAnchorFractions(angle);
  return `translate(${(-fx * 100).toFixed(2)}%, ${(-fy * 100).toFixed(2)}%)`;
}

/** Halve breedte (graden) van de "absolute" top-/bottom-sectie (ronde 5). */
const VERTICAL_SECTOR_HALF_WIDTH_DEG = 30;

/**
 * Cards van dezelfde poort staan standaard ónder elkaar (kolom, ronde 5).
 * Alleen in de absolute top-/bottom-sectie is verticale ruimte schaars
 * (scrollbar-gevaar): daar mogen maximaal 2 cards boven elkaar — vanaf 3
 * wordt het een grid van 2 rijen dat horizontaal aangroeit.
 */
function stackLayout(angle: number, cardCount: number): 'column' | 'grid' {
  if (cardCount < 3) {
    return 'column';
  }
  const normalized = normalizeAngle(angle);
  const nearTop = normalized < VERTICAL_SECTOR_HALF_WIDTH_DEG || normalized >= 360 - VERTICAL_SECTOR_HALF_WIDTH_DEG;
  const nearBottom = normalized >= 180 - VERTICAL_SECTOR_HALF_WIDTH_DEG
    && normalized < 180 + VERTICAL_SECTOR_HALF_WIDTH_DEG;
  return nearTop || nearBottom ? 'grid' : 'column';
}
